import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import { Plus, X } from "@phosphor-icons/react";
import {
  TICKET_STATUSES, TICKET_CATEGORIES,
  ticketStatusLabel, ticketCategoryLabel, ticketStatusVariant, formatIso,
} from "../../lib/tickets";

function NewTicketModal({ onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general_support");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(null);

  const submit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/support-tickets", {
        subject: subject.trim(), category, body: body.trim(),
      });
      setAck(data.acknowledgement || "Ticket opened.");
      if (onCreated) onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open ticket.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="ticket-new-modal"
    >
      <div className="bg-white w-full max-w-xl rounded shadow-lg border border-zinc-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="overline text-[10px]">SUPPORT</div>
            <div className="font-medium text-sm">New ticket</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
            <X size={16} />
          </button>
        </div>

        {ack ? (
          <div className="px-5 py-6" data-testid="ticket-new-ack">
            <div className="overline text-[10px] text-emerald-600 mb-2">OPENED</div>
            <div className="text-sm text-zinc-800">{ack}</div>
            <div className="mt-5 flex justify-end">
              <button onClick={onClose} className="gf-btn gf-btn-primary">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4">
            <div>
              <label className="overline text-[10px]">CATEGORY</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="gf-input w-full"
                data-testid="ticket-new-category"
              >
                {TICKET_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline text-[10px]">SUBJECT</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="gf-input w-full"
                placeholder="Short summary"
                data-testid="ticket-new-subject"
              />
            </div>
            <div>
              <label className="overline text-[10px]">MESSAGE</label>
              <textarea
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="gf-input w-full"
                placeholder="What's going on? Include any reference numbers if you have them."
                data-testid="ticket-new-body"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="gf-btn gf-btn-outline">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="gf-btn gf-btn-primary"
                data-testid="ticket-new-submit"
              >
                {busy ? "Sending…" : "Open ticket"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerSupportTickets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showNew, setShowNew] = useState(false);

  const reload = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    api.get("/portal/support-tickets", { params })
      .then(({ data }) => setRows(data || []))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [statusFilter]);

  return (
    <>
      <PageHeader
        title="Support"
        subtitle="PORTAL / SUPPORT"
        testId="portal-support-header"
        actions={
          <button
            onClick={() => setShowNew(true)}
            className="gf-btn gf-btn-primary"
            data-testid="portal-support-new-btn"
          >
            <Plus size={14} /> New ticket
          </button>
        }
      />

      <div className="gf-card mb-4">
        <div className="gf-card-body flex items-center justify-between gap-3">
          <div className="text-sm text-zinc-600">
            Open a ticket for billing questions, card issues, transaction
            discrepancies, or anything else you need help with. We aim to
            reply within one business day.
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="gf-input"
            data-testid="portal-support-status-filter"
          >
            <option value="">All statuses</option>
            {TICKET_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="gf-card">
        <table className="gf-table" data-testid="portal-support-table">
          <thead>
            <tr>
              <th>OPENED</th>
              <th>SUBJECT</th>
              <th>CATEGORY</th>
              <th>STATUS</th>
              <th>LAST UPDATE</th>
              <th>LAST REPLY</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center text-zinc-400 py-10">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="text-center text-zinc-400 py-10"
                  data-testid="portal-support-empty"
                >
                  No tickets yet. Click <strong>New ticket</strong> to get started.
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr
                key={t.ticket_id}
                className="cursor-pointer hover:bg-zinc-50"
                data-testid={`portal-support-row-${t.ticket_id}`}
              >
                <td className="font-mono text-xs">{formatIso(t.opened_at)}</td>
                <td className="text-sm">
                  <Link
                    to={`/portal/support/${t.ticket_id}`}
                    className="font-medium text-zinc-900 hover:text-orange-600"
                    data-testid={`portal-support-link-${t.ticket_id}`}
                  >
                    {t.subject}
                  </Link>
                  <div className="text-xs text-zinc-400 truncate max-w-md">
                    {t.last_message_preview}
                  </div>
                </td>
                <td>
                  <Badge variant="info">{ticketCategoryLabel(t.category)}</Badge>
                </td>
                <td>
                  <Badge variant={ticketStatusVariant(t.status)}>
                    {ticketStatusLabel(t.status)}
                  </Badge>
                </td>
                <td className="font-mono text-xs">{formatIso(t.last_message_at)}</td>
                <td className="overline text-[10px]">
                  {t.last_message_role || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewTicketModal
          onClose={() => { setShowNew(false); reload(); }}
          onCreated={reload}
        />
      )}
    </>
  );
}
